import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core'
import { Store } from '@ngrx/store'
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs'
import { concatMap, map, tap } from 'rxjs/operators'
import * as R from 'ramda'
import * as moment from 'moment'

import * as fromRoot from '@tv3/store/state'
import { HasPermission } from '@tokeet-frontend/permission'
import { MultiCalendarEventView } from '@tv3/store/calendar/calendar.view'
import { Destroyable, isSomething, Rental, selectSome, untilDestroy } from '@tokeet/tv3-platform'
import { EditHoldDialogService } from '@tv3/containers/calendar/hold/edit-hold-dialog/edit-hold-dialog.service'
import { MessageSidePanelService } from '@tv3/containers/inquiries/dialogs/message/message-side-panel.service'
import { NotesSidePanelService } from '@tv3/containers/inquiries/dialogs/notes/notes/notes-side-panel.service'
import { Inquiry, InquiryPaymentStatus, InquirySourceType } from '@tv3/store/inquiry/inquiry.model'
import { BOOKING_DETAILS_DATA } from '@tv3/containers/calendar/shared/booking-details-popup/booking-details-popup.token'
import { bookingDetailsPopupAnimation } from '@tv3/animations/booking-details-popup.animation'
import { OpenGuestOverlay, OpenInquiryOverlay, OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { selectInquiry } from '@tv3/store/inquiry/inquiry.selectors'
import { MarkPaidInquiry, MarkUnpaidInquiry, SetInquiryColor } from '@tv3/store/inquiry/inquiry-fields.actions'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { FeeTypes, InquiryCost } from '@tokeet/cost-resolver'
import { FeeModalityLabels } from '@tokeet-frontend/bookings'
import { InquirySharedDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-shared-dialogs.service'
import { AuthService } from '@tv3/services/auth.service'
import { NgxPermissionsService } from 'ngx-permissions'

@Component({
  selector: 'app-booking-details-popup',
  templateUrl: './booking-details-popup.component.html',
  animations: [bookingDetailsPopupAnimation.popupState],
  host: { class: 'multi-calendar-page popover-event-details booking-details-popup' },
  styleUrls: ['./booking-details-popup.component.scss'],
})
export class BookingDetailsPopupComponent extends Destroyable implements OnInit {
  event: MultiCalendarEventView
  rental: Rental
  inquiry: Inquiry
  checkin: moment.Moment
  checkout: moment.Moment

  paymentStatus$: Observable<{ paid: boolean; remaining: number; status: InquiryPaymentStatus }>

  currencySymbol: string
  currencyCode: string

  _visibility: 'initial' | 'visible' | 'hidden' = 'initial'

  private readonly _onHide: Subject<any> = new Subject()

  isInitializing = true

  isDialog = false

  charges$: Observable<InquiryCost>

  isTokeetPlatform = false

  feeTypes = FeeTypes
  feeModalityLabels = FeeModalityLabels

  get currentUser() {
    return this.auth.user
  }
  get isOwner() {
    return this.auth.hasRole('owner')
  }

  canSeeCharges$ = new BehaviorSubject(false)

  hasPermission$(permissions: NgxPermissionsService, per: string | string[]) {
    return from(permissions.hasPermission(per))
  }

  constructor(
    @Inject(BOOKING_DETAILS_DATA) @Optional() public data: any,
    @Inject(MAT_DIALOG_DATA) @Optional() public dialogData: any,
    @Optional() public dialogRef: MatDialogRef<BookingDetailsPopupComponent>,
    private _changeDetectorRef: ChangeDetectorRef,
    private editHoldDialog: EditHoldDialogService,
    private messageSidePanel: MessageSidePanelService,
    private noteSidePanel: NotesSidePanelService,
    private inquiryChargeService: InquiryChargeService,
    private store: Store<fromRoot.State>,
    public inquiryService: InquiryService,
    private inquirySharedDialogsService: InquirySharedDialogsService,
    public permissions: NgxPermissionsService,
    private auth: AuthService
  ) {
    super()
  }

  ngOnInit() {
    const data = this.data ? this.data : this.dialogData

    const canSeeCharges$ = () => {
      if (this.isOwner) {
        return of(!!this.currentUser?.settings?.total)
      } else {
        return this.hasPermission$(this.permissions, 'canEditInquiry')
      }
    }
    canSeeCharges$().subscribe((canSeeCharges) => {
      this.canSeeCharges$.next(canSeeCharges)
    })

    this.event = data.event
    this.rental = data.rental
    this.inquiry = data.inquiry
    this.checkin = data.checkin
    this.checkout = data.checkout

    this.isTokeetPlatform =
      isSomething(this.inquiry) &&
      (this.inquiry?.inquirySource?.startsWith('tokeet') ||
        this.inquiry?.inquirySource?.startsWith('hometogo') ||
        this.inquiry?.inquirySource?.startsWith('webready')) &&
      isSomething(this.inquiry.bookingEngine)

    if (this.dialogData) {
      this.show()
      this.isDialog = true
    }

    this.currencySymbol = R.pathOr('$', ['currency', 'symbol'], this.rental)
    this.currencyCode = R.pathOr('$', ['currency', 'code'], this.rental)

    setTimeout(() => {
      this.isInitializing = false
    }, 500)

    this.store.pipe(selectSome(selectInquiry, { id: this.inquiry.id }), untilDestroy(this)).subscribe((inquiry) => {
      this.inquiry = inquiry
    })

    this.paymentStatus$ = this.inquiryChargeService.getBookingPaymentStatus(this.inquiry.id)

    this.charges$ = this.store.pipe(
      selectSome(selectInquiry, { id: this.inquiry.id }),
      concatMap((inquiry) => this.inquiryChargeService.getCharges(inquiry).pipe(map((inquiry) => inquiry.charges)))
    )
  }

  potentialFees() {
    return this.inquiry?.bookingEngine?.bookingFees?.filter((fee) => !fee['upfront'])
  }

  tryToGetFormulaCost(inquiryService: InquiryService, inquiry: Inquiry) {
    return inquiryService.getCostAfterFormula(inquiry.id)
  }

  getPaymentStatusClass(status: InquiryPaymentStatus) {
    if (status === InquiryPaymentStatus.Paid) return 'paid'
    if (status === InquiryPaymentStatus.Unpaid) return 'unpaid'
    if (status === InquiryPaymentStatus.PartialPaid) return 'partial-paid'
  }

  getPaymentStatusTooltip(inquiry: Inquiry) {
    switch (inquiry.sourceType) {
      case InquirySourceType.Airbnb:
      case InquirySourceType.Vrbo:
      case InquirySourceType.FlipKey:
        // tslint:disable-next-line:max-line-length
        return 'Payment status for this channel is taken directly from the originating channel.  When a booking is paid on the channel website, this status will update accordingly.'
      case InquirySourceType.Booking:
      case InquirySourceType.Expedia:
      case InquirySourceType.Agoda:
        // tslint:disable-next-line:max-line-length
        return 'The payment status reflected here does not account for any payments made outside of Tokeet invoicing.  Once the total of paid Tokeet invoices equals the booking charges shown on the booking, the status will automatically change to paid.  If you do not want to invoice through Tokeet, you can manually mark the booking as paid by using the Actions button on the booking details overlay.'
      default:
        // tslint:disable-next-line:max-line-length
        return 'The payment status reflected here does not account for any payments made on the channel listing site.  You can either create an invoice that equals the booking charges shown or manually mark the booking as paid by using the Actions button on the booking details overlay.'
    }
  }

  @HasPermission('canEditHold')
  onEditHold() {
    this.editHoldDialog.open(this.event, true, true)
  }

  onOpenMessages() {
    this.messageSidePanel.open(this.inquiry.id)
  }

  onOpenNotes() {
    this.noteSidePanel.open(this.inquiry)
  }

  onOpenInquiry() {
    this.store.dispatch(OpenInquiryOverlay({ inquiryId: this.inquiry.id }))
  }

  onOpenRental() {
    this.store.dispatch(OpenRentalOverlay({ rental: this.rental }))
  }

  onEditGuest() {
    this.store.dispatch(OpenGuestOverlay({ guestId: this.inquiry.guestId }))
  }

  onClone() {
    this.inquirySharedDialogsService.cloneInquiry(this.inquiry)
  }

  onChangePaymentStatus(currentStatus: InquiryPaymentStatus) {
    if (currentStatus === InquiryPaymentStatus.Paid) {
      this.onUnPaid()
    } else {
      this.onPaid()
    }
  }

  onPaid() {
    this.store.dispatch(MarkPaidInquiry({ inquiry: this.inquiry }))
  }

  onUnPaid() {
    this.store.dispatch(MarkUnpaidInquiry({ inquiry: this.inquiry }))
  }

  onClose() {
    if (this.isDialog && this.dialogRef) {
      this.dialogRef.close()
    } else {
      this.hide()
    }
  }

  onClickOutside(event) {
    if (this.isInitializing) {
      return
    }
    if (R.is(Function, (<any>event.target).closest)) {
      const closest = (<any>event.target).closest('.popover-booking')
      if (R.isNil(closest)) {
        this.hide()
      }
    }
  }

  onChangeInquiryColor(color) {
    this.store.dispatch(SetInquiryColor({ inquiry: this.inquiry, color }))
  }

  isVisible(): boolean {
    return this._visibility === 'visible'
  }

  show() {
    this._visibility = 'visible'
    this._markForCheck()
  }

  hide() {
    this._visibility = 'hidden'
    this._onHide.next(this._visibility)
    this._onHide.complete()
    this._markForCheck()
  }

  afterHidden(): Observable<void> {
    return this._onHide.asObservable()
  }

  _markForCheck(): void {
    this._changeDetectorRef.markForCheck()
  }

  _animationDone(event): void {
    const toState = event.toState as 'initial' | 'visible' | 'hidden'

    if (toState === 'hidden' && !this.isVisible()) {
      this._onHide.next()
    }
  }

  ngOnDestroy() {
    this._onHide.next(true)
    this._onHide.complete()
  }
}
